import React, { useEffect, useState } from 'react';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { AuthenticatedLayout } from './AuthenticatedLayout';
import PublicLayout from './PublicLayout';
import { LoadingPage } from '@/components/ui/LoadingHeader';

interface ConditionalLayoutProps {
  children: React.ReactNode;
}

export function ConditionalLayout({ children }: ConditionalLayoutProps) {
  // Safely get auth context with error handling
  let authContext;
  try {
    authContext = useSupabaseAuth();
  } catch (error) {
    console.error('ConditionalLayout: Cannot access UnifiedAuthContext - provider not found:', error);
    // Fallback to public layout if context is not available
    return (
      <div id="conditional-layout-fallback">
        <PublicLayout>{children}</PublicLayout>
      </div>
    );
  }
  
  const { user, isInitialized, isLoading } = authContext;
  const [forceUpdate, setForceUpdate] = useState(0);

  // Force re-render when auth state changes to ensure proper layout switching
  useEffect(() => {
    setForceUpdate(prev => prev + 1);
  }, [user, isInitialized]);

  // Show loading page while authentication is initializing
  if (!isInitialized || isLoading) {
    return <LoadingPage />;
  }

  // Use authenticated layout with sidebar for logged in users
  if (user) {
    return (
      <div id="conditional-layout-authenticated">
        <AuthenticatedLayout key={`auth-${forceUpdate}`}>
          {children}
        </AuthenticatedLayout>
      </div>
    );
  }

  // Use public layout for public pages
  return (
    <div id="conditional-layout-public">
      <PublicLayout key={`public-${forceUpdate}`}>
        {children}
      </PublicLayout>
    </div>
  );
}
